import React, { useState, useEffect } from 'react';
import MonacoEditor from 'react-monaco-editor';
import { FileContent } from '../types';
import { isValidHtml, generateDefaultFilename } from '../utils/html';

interface CodeEditorProps {
  initialValue?: string;
  onChange?: (content: FileContent) => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({ 
  initialValue = '', 
  onChange 
}) => {
  const [content, setContent] = useState<string>(initialValue);
  const [filename, setFilename] = useState<string>('');
  const [isHtml, setIsHtml] = useState<boolean>(false);
  
  // Set language based on file extension or content analysis
  const [language, setLanguage] = useState<string>('text');
  
  // Handle content changes
  useEffect(() => {
    // Check if content is HTML
    const htmlCheck = isValidHtml(content);
    setIsHtml(htmlCheck);
    
    // Set language based on content
    if (htmlCheck) {
      setLanguage('html');
    } else if (content.includes('function') || content.includes('const') || content.includes('var')) {
      setLanguage('javascript');
    } else {
      setLanguage('text');
    }
    
    // Generate default filename if none is set
    if (!filename) {
      setFilename(generateDefaultFilename(content));
    }
    
    // Call onChange handler
    if (onChange) {
      onChange({
        content,
        filename,
        isHtml: htmlCheck
      });
    }
  }, [content, filename, onChange]);
  
  // Handle editor change
  const handleEditorChange = (value: string) => {
    setContent(value);
  };
  
  // Handle filename change
  const handleFilenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilename(e.target.value);
  };
  
  // Editor options
  const options = {
    selectOnLineNumbers: true,
    roundedSelection: false,
    readOnly: false,
    cursorStyle: 'line',
    automaticLayout: true,
    minimap: { enabled: false },
  };
  
  return (
    <div className="code-editor-container">
      <div className="mb-3">
        <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-1">
          Filename
        </label>
        <input
          type="text"
          id="filename"
          value={filename}
          onChange={handleFilenameChange}
          placeholder="Enter filename"
          className="px-3 py-2 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        />
      </div>
      
      <div className="code-editor">
        <MonacoEditor
          width="100%"
          height="400"
          language={language}
          theme="vs-dark"
          value={content}
          options={options}
          onChange={handleEditorChange}
        />
      </div>
    </div>
  );
};

export default CodeEditor; 